import React, { FC } from 'react'
import { DecryptedPatient } from '@icure/cardinal-sdk'
import { getPatientDataFormated } from '../../../../../../helpers/patientDataManipulations'
import { Button, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'

import { useTranslation } from 'react-i18next'

import './index.less'
import { useCreateOrUpdatePatientMutation } from '../../../../../../core/api/patientApi'
import { getPreparedTagsToUpdatePatient } from '../../../../PatientsDirectory/modals/ModalAddPatientTag/helpers'
import { TagWithActionConfirm } from '../TagWithActionConfirm'

interface IntroContentProps {
  patient?: DecryptedPatient
  onAddTag: () => void
}

const { Paragraph } = Typography

export const IntroContent: FC<IntroContentProps> = ({ patient, onAddTag }) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const { userDateOfBirthOneString, userHomeAddressOneString, age, birthSex, phoneNumber, emailAddress, tags, ssin, idCardNumber } =
    getPatientDataFormated(patient, t, currentLanguage)
  const [updatePatient, { error: updatePatientError, isError: isUpdatePatientError, isSuccess: isPatientUpdatedSuccessfully, isLoading: isPatientUpdatingLoading }] =
    useCreateOrUpdatePatientMutation()

  const patientInfo = [
    {
      title: t('patientInfo.personal.title'),
      data: [
        {
          isCopiable: false,
          label: t('patientInfo.personal.dateOfBirth'),
          value: userDateOfBirthOneString,
        },
        {
          isCopiable: false,
          label: t('patientInfo.personal.age'),
          value: age,
        },
        {
          isCopiable: false,
          label: t('patientInfo.personal.birthSex'),
          value: birthSex,
        },
      ],
    },
    {
      title: t('patientInfo.admin.title'),
      data: [
        {
          isCopiable: true,
          label: t('patientInfo.admin.idCardNumber'),
          value: idCardNumber,
        },
        {
          isCopiable: true,
          label: t('patientInfo.admin.ssin'),
          value: ssin,
        },
      ],
    },
    {
      title: t('patientInfo.contact.title'),
      data: [
        {
          isCopiable: true,
          label: t('patientInfo.contact.phone'),
          value: phoneNumber,
        },
        {
          isCopiable: true,
          label: t('patientInfo.contact.email'),
          value: emailAddress,
        },
        {
          isCopiable: true,
          label: t('patientInfo.contact.address'),
          value: userHomeAddressOneString,
        },
      ],
    },
  ]

  const handleDeleteTag = (tagValue: string) => {
    if (!tags) return

    const updatedTags = tags.map(({ code }) => code).filter((code): code is string => !!code && code !== tagValue)

    updatePatient(
      new DecryptedPatient({
        ...patient,
        ...getPreparedTagsToUpdatePatient(updatedTags),
      }),
    )
  }

  return (
    <div className="patientIntroContent">
      <div className="patientIntroContent__infoBlocks">
        {patientInfo.map((block, index) => (
          <div key={index} className="patientInfo">
            <h4>{block.title}</h4>
            <div key={index} className="patientInfo__content">
              {block.data.map((dataEl, dataElIndex) => (
                <div key={dataElIndex} className="patientInfo__content__indicators">
                  <h5>{dataEl.label}</h5>

                  {dataEl.isCopiable && dataEl.value ? (
                    <Paragraph copyable style={{ margin: 0 }}>
                      {dataEl.value}
                    </Paragraph>
                  ) : (
                    <p>{dataEl.value ?? t('patientInfo.notProvided')}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="patientIntroContent__actionBlocks">
        <div className="tags">
          <h4>{t('patientInfo.tags.title')}</h4>
          <div className="tags__content">
            {!!tags && tags.length !== 0 && (
              <div className="tags__content__list">
                {tags.map(({ code }, index) => code !== undefined && <TagWithActionConfirm key={index} code={code} onDeleteTag={() => handleDeleteTag(code)} />)}
              </div>
            )}
            <Button icon={<PlusOutlined />} size="small" onClick={onAddTag}>
              {t('patientInfo.tags.add')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
